import _, {truncate} from 'lodash';
import React, {FC, useRef, useState} from 'react';
import styled, {css} from 'styled-components';

import {TimeFormatsEnum} from '../../../../../../../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {useLocalize} from '../../../../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {
  defineMessage,
  Trans,
} from '../../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {AsyncStatusesEnum} from '../../../../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {TeammateModel} from '../../../../../../../../libs/shared/core/src/models/api/teammateModel';
import {DataEventActionsEnum} from '../../../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {CommonSettingsDataEventObjectsEnum} from '../../../../../../../../libs/shared/core/src/models/tracking/objectNames/settingsDataEventObject';
import {HttpResponseError} from '../../../../../../../../libs/shared/core/src/services/http/httpError';
import {useDataEventTracking} from '../../../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {IconButton} from '../../../../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {
  RelativeDateTimeFormatsEnum,
  RelativeDateTimeFormatter,
} from '../../../../../../../../libs/shared/ui/src/components/datetime/relativeDateTimeFormatter';
import {Icon} from '../../../../../../../../libs/shared/ui/src/components/icon/icon';
import {Loader} from '../../../../../../../../libs/shared/ui/src/components/loaders/loader';
import {useLinkNavigation} from '../../../../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {MultilineTooltipOverflowCondition} from '../../../../../../../../libs/shared/ui/src/components/tooltips/multilineTooltipOverflowCondition';
import {TooltipCoordinator} from '../../../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {
  Skeleton,
  SkeletonColorSchemesEnum,
} from '../../../../../../../../libs/shared/ui/src/components/utility/skeleton';
import {VisualSizesEnum} from '../../../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis, ellipsisAfterNumberOfLines} from '../../../../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {DateTimeFormatsEnum} from '../../../../../../../../srcCommon/formatters/dateTime/dateTimeFormatter';
import {useAppDispatch} from '../../../../../../../helpers/appReduxHelpers';
import {ApplicationModel} from '../../../../../../../models/api/applicationModel';
import {TopicModel} from '../../../../../../../models/api/topicModel';
import {refreshTopicAsyncAction} from '../../../../../../../store/data/conversations/topicConversationsActions';
import {InvertedDateTimeFormatter} from '../../../../../../common/formatters/dateTime/invertedDateTimeFormatter';
import {
  AnnotationViewerReferencePreviewCopyButton,
  StyledCopyIconWrapper,
} from './annotationViewerReferencePreviewCopyButton';
import {CommentViewerReferencePreviewerUnfurlIcon} from './commentViewerReferencePreviewerUnfurlIcon';

/*
 * Constants.
 */

export enum TopicReferencePreviewerUnfurlTypesEnum {
  COMMENT = 'comment',
  MESSAGE = 'message',
  CONVERSATION_SUMMARY = 'conversation_summary',
}

export interface CustomFieldData {
  name: string;
  value: string;
  id: number;
}

const maxCustomFieldLength = 250;
const horizontalPadding = 16;
const numInitialCustomFields = 5;

/*
 * Props.
 */

type TopicAppObjectModel = Pick<TopicModel, 'id' | 'name' | 'description' | 'externalLink' | 'refreshedAt'>;

interface TopicReferencePreviewerUnfurlProps extends TopicReferencePreviewerUnfurlBaseProps {
  type: TopicReferencePreviewerUnfurlTypesEnum;
  // These props are applicable when the type is MESSAGE and the previewer unfurl is possibly sandwiched
  // under a url and/or above a comment.
  isAboveComment?: boolean;
  isUnderUrl?: boolean;
}

export interface TopicReferencePreviewerUnfurlBaseProps {
  className?: string;
  topic?: TopicAppObjectModel;
  owner?: TeammateModel;
  application?: ApplicationModel;
  customFieldDataArray: ReadonlyArray<CustomFieldData>;
  dateTimeFormat: DateTimeFormatsEnum;
  timeFormat: TimeFormatsEnum;
  isReadOnly?: boolean;
}

/*
 * Style.
 */

interface StyledPreviewerWrapperDivProps {
  $type: TopicReferencePreviewerUnfurlTypesEnum;
}

interface StyledPreviewerContainerDivProps {
  $type: TopicReferencePreviewerUnfurlTypesEnum;
  $isUnderUrl: boolean;
  $isAboveComment: boolean;
}

interface ConversationSummaryProps {
  $isConversationSummary: boolean;
}

const StyledPreviewerWrapperDiv = styled.div<StyledPreviewerWrapperDivProps>`
  ${(p) => computePreviewerWrapperDivStyles(p.$type)}
`;

interface ScrollableDivProps {
  $isUnderUrl: boolean;
  $isAboveComment: boolean;
  $maxHeight?: number;
}

const ScrollableDiv = styled.div<ScrollableDivProps>`
  overflow-y: auto;
  overflow-x: hidden;
  max-height: ${(p) => p.$maxHeight ?? 238}px;
`;

function computePadding(
  type: TopicReferencePreviewerUnfurlTypesEnum,
  isUnderUrl: boolean,
  isAboveComment: boolean,
) {
  if (
    type === TopicReferencePreviewerUnfurlTypesEnum.COMMENT ||
    type === TopicReferencePreviewerUnfurlTypesEnum.CONVERSATION_SUMMARY
  ) {
    return `${horizontalPadding}px`;
  }

  // Topic annotation, url on top, and comment below.
  if (isUnderUrl && isAboveComment) {
    return `8px ${horizontalPadding}px 8px ${horizontalPadding}px`;
  }
  // Only topic annotation and url on top.
  if (isUnderUrl) {
    return `8px ${horizontalPadding}px 10px ${horizontalPadding}px`;
  }
  // Only topic annotation and comment below.
  if (isAboveComment) {
    return `0 ${horizontalPadding}px 8px ${horizontalPadding}px`;
  }
  // Only topic annotation.
  return `6px ${horizontalPadding}px ${horizontalPadding}px ${horizontalPadding}px`;
}

function computePreviewerWrapperDivStyles(type: TopicReferencePreviewerUnfurlTypesEnum) {
  if (type === TopicReferencePreviewerUnfurlTypesEnum.COMMENT) {
    return css`
      position: relative;
      background-color: ${(p) => p.theme.greys.white};

      margin: 4px 12px 12px 12px;
      max-width: 368px;
      border-radius: 6px;
      max-height: 430px;
    `;
  }
  if (type === TopicReferencePreviewerUnfurlTypesEnum.CONVERSATION_SUMMARY) {
    return css`
      position: relative;
      background-color: ${(p) => p.theme.greys.white};

      border-radius: 6px;
      max-height: 430px;
    `;
  }
  return css`
    max-height: 340px;
  `;
}

interface StyledPreviewerDivProps extends ConversationSummaryProps {
  $shouldAddBottomPadding: boolean;
}

const StyledPreviewerDiv = styled.div<StyledPreviewerDivProps>`
  display: grid;
  grid-template-columns: min-content 1fr min-content;
  grid-template-rows: 20px;
  align-items: center;

  border-radius: 6px;
  gap: 4px;
  padding: ${(p) => (p.$shouldAddBottomPadding ? `0 0 8px 0` : ``)};
`;

export const StyledHeaderLinkDiv = styled.div`
  color: ${(p) => p.theme.palette.blue.shade40};
  font-size: ${fontSizes.medium};
  overflow: hidden;
  font-weight: ${fontWeights.semibold};

  &:hover {
    color: ${(p) => p.theme.palette.blue.shade50};
  }
`;

interface StyledDescriptionWrapperDivProps extends ConversationSummaryProps {
  $hasCustomFields: boolean;
}

const StyledDescriptionWrapperDiv = styled.div<StyledDescriptionWrapperDivProps>`
  margin: ${(p) => (p.$hasCustomFields ? `0 0 8px 0` : ``)};
  height: 100%;
  white-space: normal;
  word-wrap: break-word;

  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  line-height: 17px;
  text-align: left;
  ${ellipsisAfterNumberOfLines(3)};
`;

const CustomFieldsWrapper = styled.div`
  position: relative;
`;

const NameDiv = styled.div`
  text-align: left;
  margin: 5px 0;

  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade70};
  ${ellipsis()};
`;

const ValueDiv = styled.div`
  display: flex;
`;

const StyledValueDiv = styled.div`
  margin: 5px 0;
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  text-align: left;

  ${ellipsis()};
`;

const CustomFieldDiv = styled.div`
  display: grid;
  line-height: 16px;
  grid-template-columns: 1fr minmax(0, 2fr);
  gap: 12px;

  &:hover {
    ${StyledCopyIconWrapper} {
      display: flex;
    }
  }
`;

const TooltipContentWrapper = styled.div`
  word-wrap: break-word;
`;

const FooterContentDiv = styled.div`
  font-size: 14px;
  color: ${(p) => p.theme.greys.shade60};
  padding: ${horizontalPadding}px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 52px;
  border-top: 1px solid ${(p) => p.theme.greys.shade40};
`;

const StyledPreviewerContainer = styled.div<StyledPreviewerContainerDivProps>`
  display: flex;
  flex-direction: column;
  padding: ${(p) => computePadding(p.$type, p.$isUnderUrl, p.$isAboveComment)};
`;

const StyledUpdateDateDiv = styled.div`
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.normal};
  line-height: 14.32px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StyledUpdateErrorText = styled.div`
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.normal};
  line-height: 14.32px;
  color: ${(p) => p.theme.palette.red.shade40};
`;

const StyleCustomFieldLoadingDiv = styled.div`
  display: grid;
  line-height: 16px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  padding: 6px 0;
`;

const StyledSkeleton = styled(Skeleton)`
  margin-top: 8px;
  margin-bottom: 8px;
`;

/*
 * Component.
 */

export const TopicReferencePreviewerUnfurl: FC<TopicReferencePreviewerUnfurlProps> = (props) => {
  const {application, topic, className, customFieldDataArray, type, isAboveComment, isUnderUrl, isReadOnly} =
    props;

  const theme = useUserTheme();

  const [refreshStatus, setRefreshStatus] = useState(AsyncStatusesEnum.LOADED);

  const scrollHeightRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const localize = useLocalize();

  const defaultErrorMessage = localize(
    defineMessage({
      id: 'topic-referencepreviewer-failed-to-refresh',
      message: 'Failed to refresh data. Please try again.',
    }),
  );
  const [errorMessage, setErrorMessage] = useState(defaultErrorMessage);

  const hasCustomFields = customFieldDataArray.length > 0;
  const {navigateToLink} = useLinkNavigation();
  const {trackDataEvent} = useDataEventTracking();

  const [shouldShowCheckmark, setShouldShowCheckmark] = useState(false);

  if (!topic) {
    return null;
  }

  const isConversationSummary = type === TopicReferencePreviewerUnfurlTypesEnum.CONVERSATION_SUMMARY;

  const onRefreshClickAsync = async () => {
    if (isReadOnly) {
      return;
    }

    try {
      setRefreshStatus(AsyncStatusesEnum.LOADING);
      await dispatch(refreshTopicAsyncAction(topic.id));
      setRefreshStatus(AsyncStatusesEnum.LOADED);
    } catch (error) {
      if (error instanceof HttpResponseError) {
        setErrorMessage(error.reason);
      }
      setRefreshStatus(AsyncStatusesEnum.FAILED);
    }
  };

  const maybeRenderDescription = () => {
    if (!topic.description) {
      return null;
    }

    if (refreshStatus === AsyncStatusesEnum.LOADING) {
      return (
        <StyledSkeleton
          borderRadius="4px"
          scheme={SkeletonColorSchemesEnum.GREY10}
          hollowContent={topic.description}
        />
      );
    }

    return (
      <StyledDescriptionWrapperDiv
        $hasCustomFields={hasCustomFields}
        $isConversationSummary={isConversationSummary}
      >
        {topic.description}
      </StyledDescriptionWrapperDiv>
    );
  };

  const renderCustomField = (id: number, name: string, value: string) => {
    if (refreshStatus === AsyncStatusesEnum.LOADING) {
      return (
        <StyleCustomFieldLoadingDiv key={id}>
          <Skeleton width="80%" height="11px" borderRadius="4px" scheme={SkeletonColorSchemesEnum.GREY10} />
          <Skeleton width="80%" height="11px" borderRadius="4px" scheme={SkeletonColorSchemesEnum.GREY10} />
        </StyleCustomFieldLoadingDiv>
      );
    }
    return (
      <CustomFieldDiv onMouseLeave={() => setShouldShowCheckmark(false)} key={id}>
        <NameDiv>
          <TooltipCoordinator
            isMultiline
            render={() => (
              <TooltipContentWrapper>{truncate(name, {length: maxCustomFieldLength})}</TooltipContentWrapper>
            )}
            placement="top"
          >
            <MultilineTooltipOverflowCondition lines={2}>{name}</MultilineTooltipOverflowCondition>
          </TooltipCoordinator>
        </NameDiv>
        <ValueDiv>
          <StyledValueDiv>
            <TooltipCoordinator
              isMultiline
              render={() => (
                <TooltipContentWrapper>
                  {truncate(value, {length: maxCustomFieldLength})}
                </TooltipContentWrapper>
              )}
              placement="top"
            >
              <MultilineTooltipOverflowCondition lines={2}>{value}</MultilineTooltipOverflowCondition>
            </TooltipCoordinator>
          </StyledValueDiv>
          <AnnotationViewerReferencePreviewCopyButton
            value={value}
            key={`${name}-${value}-${id}`}
            shouldShowCheckmark={shouldShowCheckmark}
            setShouldShowCheckmark={setShouldShowCheckmark}
          />
        </ValueDiv>
      </CustomFieldDiv>
    );
  };

  const maybeRenderInitialCustomFields = () => {
    if (!hasCustomFields) {
      return null;
    }

    return (
      <CustomFieldsWrapper>
        {_(customFieldDataArray)
          .slice(0, numInitialCustomFields)
          .map(({name, value, id}) => renderCustomField(id, name, value))
          .value()}
      </CustomFieldsWrapper>
    );
  };

  const maybeRenderRestCustomFields = () => {
    if (!hasCustomFields) {
      return null;
    }

    return (
      <CustomFieldsWrapper>
        {_(customFieldDataArray)
          .slice(numInitialCustomFields)
          .map(({name, value, id}) => renderCustomField(id, name, value))
          .value()}
      </CustomFieldsWrapper>
    );
  };

  return (
    <StyledPreviewerWrapperDiv $type={type}>
      <StyledPreviewerContainer
        $type={type}
        $isAboveComment={Boolean(isAboveComment)}
        $isUnderUrl={Boolean(isUnderUrl)}
      >
        <StyledPreviewerDiv
          className={className}
          $shouldAddBottomPadding={Boolean(topic.description)}
          $isConversationSummary={isConversationSummary}
        >
          <CommentViewerReferencePreviewerUnfurlIcon application={application} isAnnotationCard />
          <StyledHeaderLinkDiv
            onClick={() => {
              navigateToLink(topic.externalLink);
              trackDataEvent(DataEventActionsEnum.OPEN_URL, CommonSettingsDataEventObjectsEnum.ANNOTATION, {
                topic_id: topic.id,
              });
            }}
          >
            <TooltipCoordinator render={() => topic.name} placement="top">
              <TooltipOverflowCondition>{topic.name}</TooltipOverflowCondition>
            </TooltipCoordinator>
          </StyledHeaderLinkDiv>
        </StyledPreviewerDiv>
        <ScrollableDiv
          $isAboveComment={Boolean(isAboveComment)}
          $isUnderUrl={Boolean(isUnderUrl)}
          $maxHeight={scrollHeightRef.current?.offsetHeight}
        >
          <div ref={scrollHeightRef}>
            {maybeRenderDescription()}
            {maybeRenderInitialCustomFields()}
          </div>
          {maybeRenderRestCustomFields()}
        </ScrollableDiv>
      </StyledPreviewerContainer>
      <FooterContentDiv>
        {renderFooterContent(refreshStatus, errorMessage, topic, props)}
        {isReadOnly && <Icon name="repeatCircleSmall" />}
        {!isReadOnly && (
          <IconButton
            iconName="repeatCircleSmall"
            color={theme.greys.shade80}
            hoverColor={theme.greys.shade80}
            label={<Trans id="refresh-app-object-button">Refresh</Trans>}
            onClick={async (event) => {
              event.stopPropagation();
              event.preventDefault();
              await onRefreshClickAsync();
            }}
            isDisabled={refreshStatus === AsyncStatusesEnum.LOADING || isReadOnly}
          />
        )}
      </FooterContentDiv>
    </StyledPreviewerWrapperDiv>
  );
};

const renderFooterContent = (
  refreshStatus: AsyncStatusesEnum,
  errorMessage: string,
  topic: TopicAppObjectModel,
  props: TopicReferencePreviewerUnfurlProps,
) => {
  if (refreshStatus === AsyncStatusesEnum.FAILED) {
    return <StyledUpdateErrorText>{errorMessage}</StyledUpdateErrorText>;
  }
  if (refreshStatus === AsyncStatusesEnum.LOADING) {
    return (
      <StyledUpdateDateDiv>
        <Loader size={VisualSizesEnum.SMALL} />
        <Trans id="app-object-refreshing">Refreshing...</Trans>
      </StyledUpdateDateDiv>
    );
  }
  return (
    <TooltipCoordinator
      placement="top"
      render={() => (
        <InvertedDateTimeFormatter
          timeFormat={props.timeFormat}
          value={topic.refreshedAt}
          format={props.dateTimeFormat}
        />
      )}
    >
      <StyledUpdateDateDiv>
        <Trans id="app-object-last-updated">Last updated</Trans>{' '}
        <RelativeDateTimeFormatter value={topic.refreshedAt} format={RelativeDateTimeFormatsEnum.FULL} />
      </StyledUpdateDateDiv>
    </TooltipCoordinator>
  );
};
